import { Directive, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { SubscriptionService } from "../../subscription/subscription.service";
import { BuyService } from "../../buy/buy.service";
import { Model } from "../../app.model";
import { HttpClient } from "@angular/common/http";
import { CompanyService } from "../../company/company.service";
import { WorkflowCspService } from "./workflow-csp.service";
import { firstValueFrom } from "rxjs";
import { Subscription } from "../../subscription/subscription.model";
import { SaleOrder, PaymentMethodCode, PaymentDetails } from "../../buy/buy.model";
import { observable } from "@myshared/mobx-connector";
import { runInAction } from 'mobx';
import { Company } from "../../company/company.model";
import { WorkflowComponent } from "../workflow.component";

export type BaseWorkflowModel = {
    subscription?: Subscription,
    saleOrder?: SaleOrder,
    company?: Company
}

@Directive()
export abstract class WorkflowCspComponent<T extends BaseWorkflowModel> extends WorkflowComponent implements OnInit, OnDestroy {

    @observable
    public model: T;

    protected constructor(
        protected route: ActivatedRoute,
        protected router: Router,
        protected subscriptionService: SubscriptionService,
        protected buyService: BuyService,
        protected m: Model,
        protected http: HttpClient,
        protected workflowCspService: WorkflowCspService,
        protected companyService: CompanyService
    ) {
        super();
    }

    public async ngOnInit() {
        super.ngOnInit();
        runInAction(() => {
            this.model = {
                ...this.model
            }
        });

        if (this.applianceId) {
            await this.loadSubscription()
        }

        const so = this.route.snapshot.queryParamMap.get('so');

        if (!this.model.saleOrder && so) {
            await this.loadSaleOrder(so);
        }
    }

    /**
     * Always set a workflow name because navigate function need this
     */
    abstract get workflowName(): string;

    /**
     * Only if a query param is needed
     * like sale order: ?so=SO12345
     */
    protected get queryParams(): { [e:string]: any } | undefined {
        return undefined;
    }

    protected navigate(url: (string | number)[]) {
        this.router.navigate(['/do', 'csp', this.workflowName, this.applianceId, ...url], {
            queryParams: {
                ...this.queryParams,
                so: this.model.saleOrder?.name ?? undefined,
            }
        });
    }

    protected async loadSubscription(): Promise<void> {
        const subscription = await firstValueFrom(this.subscriptionService.getSubscriptionDetails(+this.applianceId));
        runInAction(() => {
            this.model.subscription = Subscription.fromJson(subscription);
        })
    }

    protected async loadCompany(): Promise<void> {
        const company = await firstValueFrom(this.companyService.getMyCompany());
        runInAction(() => {
            this.model.company = company;
        })
    }

    protected get checkSubscriptionValid(): boolean {
        if (!this.model.subscription) {
            return false;
        }

        if (this.model.subscription.isCancelled) {
            return false;
        }

        if (this.model.subscription.is_my || !this.m.account.currentUser.companyIsPC20Csp) {
            return false;
        }

        return true;
    }

    protected async setPaymentDetails({ method, bank }: PaymentDetails) {
        if (bank && bank.acc_number && method === 'sepa') {
            await firstValueFrom(this.companyService.updateBankAccount(bank));
            runInAction(() => {
                this.model.company.bankAccounts[0] = bank;
            })
        }

        Promise.resolve();
    }

    protected async loadSaleOrder(so: string): Promise<SaleOrder> {
        return await firstValueFrom(this.buyService.getSaleOrder(so));
    }

    protected get isSepa(): boolean {
        return this.paymentMethod === 'sepa';
    }

    public get paymentMethod(): PaymentMethodCode {
        return this.model?.saleOrder?.payment_method ?? this.model?.subscription?.payment_method;
    }

    protected get hasBankAccount() {
        return this.model.company.hasBankAccount;
    }

    protected get isPaymentStepNeeded() {
        return this.isSepa && !this.hasBankAccount;
    }

    public onComplete() {
        this.resetWorkflow();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
    }
}
